import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useAuthContext } from 'src/auth/hooks';
import { evaluateSubTopic, getMyEvaluations as getMyEvaluationsApi } from 'src/api/evaluation';
import { Evaluation } from 'src/models/evaluation';
import { Topic } from 'src/models/topic';
import { EvaluationContext } from './evaluation-context-types';

interface EvaluationProviderProps {
  children: React.ReactNode;
}

export const EvaluationProvider: React.FC<EvaluationProviderProps> = ({ children }) => {
  const { user } = useAuthContext();
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchMyEvaluations = useCallback(async () => {
    setLoading(true);
    const res = await getMyEvaluationsApi();
    if (res.success && res.data) {
      setEvaluations(res.data);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (user?._id) {
      fetchMyEvaluations();
    }
  }, [user?._id, fetchMyEvaluations]);

const updateItemEvaluation = useCallback(async (
  skillId: string,
  topicId: string,
  isCompleted: boolean,
  subItemId?: string | null
): Promise<Evaluation | null> => {
  if (!user?._id) return null;

  const res = await evaluateSubTopic(user._id, skillId, topicId, isCompleted, subItemId);

  if (res.success && res.data?.evaluation) {
    const updatedEval = res.data.evaluation;
    setEvaluations((prev) => {
      const exists = prev.some((e) => e._id === updatedEval._id);
      if (exists) {
        return prev.map((e) => (e._id === updatedEval._id ? updatedEval : e));
      }
      return [...prev, updatedEval];
    });
    return updatedEval;
  }
  return null;
}, [user?._id]);

const updateTopicTreeEvaluation = useCallback(async (
  skillId: string,
  topic: Topic,
  isCompleted: boolean
) => {
  try {
    if (!topic._id) return;
    await updateItemEvaluation(skillId, topic._id, isCompleted, null);
  } catch (error) {
    console.error('Failed executing topic tree update:', error);
  }
}, [updateItemEvaluation]);

  const providerValue = useMemo(() => ({
    evaluations,
    loading,
    fetchMyEvaluations,
    updateItemEvaluation,
    updateTopicTreeEvaluation,
  }), [evaluations, loading, fetchMyEvaluations, updateItemEvaluation, updateTopicTreeEvaluation]);

  return (
    <EvaluationContext.Provider value={providerValue}>
      {children}
    </EvaluationContext.Provider>
  );
};